import type React from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { useStatisticsFromUrl } from '@/hooks/useStatisticsFromUrl';
import { parseTournamentQuery } from '@/lib/tournamentQuery';
import TournamentReport from './TournamentReport';
import { PlayerNamesProvider } from '@/contexts/PlayerNamesContext';

const TournamentPage: React.FC = () => {
  const { t } = useTranslation(['tournament', 'common']);
  const { search } = useLocation();

  const parsed = useMemo(() => parseTournamentQuery(search), [search]);

  const { data, loading, error } = useStatisticsFromUrl(
    parsed?.jsonUrl ?? null,
  );

  // Tournament JSON is guaranteed to have exactly one segment by the
  // producing pipeline; if more are present, the first wins.
  const segment = data?.segments_statistics?.[0];

  // Map slot id → display name, consumed by useResolvedPlayerName
  // anywhere inside the report tree.
  const playerNamesMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (parsed) {
      map.player1 = parsed.players[0];
      map.player2 = parsed.players[1];
      map.player3 = parsed.players[2];
      map.player4 = parsed.players[3];
    }
    return map;
  }, [parsed]);

  const renderBody = () => {
    if (!parsed) return <ErrorScreen message={t('tournament:missingParams')} />;
    if (loading) return <LoadingScreen />;
    if (error || !data || !segment) {
      return <ErrorScreen message={t('common:somethingWentWrong')} />;
    }

    // No selection step — the report opens directly and the focus player is
    // switched from the team header at the top.
    return (
      <PlayerNamesProvider names={playerNamesMap}>
        <TournamentReport segment={segment} />
      </PlayerNamesProvider>
    );
  };

  // The entire /tournament tree lives inside the FIP theme scope so the
  // CSS variables, fonts, and sky→cyan overrides defined in
  // src/styles/fip-theme.css take effect; nothing outside this route is
  // affected.
  return <div className="fip-theme min-h-[100dvh]">{renderBody()}</div>;
};

const LoadingScreen: React.FC = () => (
  <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-[color:var(--fip-surface)]">
    <div
      className="h-8 w-8 animate-spin border-2 border-[color:var(--fip-line)] border-t-[color:var(--fip-navy)]"
      style={{ borderRadius: '50%' }}
    />
  </div>
);

const ErrorScreen: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-[color:var(--fip-surface)] p-6 text-center">
    <AlertCircle className="h-8 w-8 text-[color:var(--fip-muted)]" />
    <p
      className="text-sm uppercase tracking-[0.08em] text-[color:var(--fip-ink)]"
      style={{ fontFamily: 'Oswald, sans-serif' }}
    >
      {message}
    </p>
  </div>
);

export default TournamentPage;
